import dayjs from "dayjs";
import { getInternalApiInstance } from "./base";

export const userSearchRequest = async (q: string) => {
  const api = await getInternalApiInstance();
  try {
    const res = await api.post("/admin/search-user-email-or-sub", { q });
    return res.data.data;
  } catch (error) {
    throw error;
  }
}

export const userDetailsRequest = async (username: string) => {
  const api = await getInternalApiInstance();
  try {
    const res = await api.get("/admin/user/" + username);
    return res.data.data;
  } catch (error) {
    throw error;
  }
}

export const resendTemporaryPasswordRequest = async ({
  username,
}: { username: string }) => {
  const api = await getInternalApiInstance();
  const reqBody = {
    username,
  }
  try {
    const res = await api.post("/admin/resend-temporary-password", { ...reqBody });
    return res.data.data;
  } catch (error) {
    throw error;
  }
}

export const forceRequestPasswordRequest = async ({
    email,
}: {
    email: string;
}) => {
    const api = await getInternalApiInstance();
    const reqBody = {
        email,
    };
    try {
        const res = await api.post("/admin/force-reset-password", {
            ...reqBody,
        });
        return res.data.data;
    } catch (error) {
        throw error;
    }
};

export const createPilotRequest = async ({ email, name, family_name }: {
  email: string;
  name: string;
  family_name: string;
}) => {
  const api = await getInternalApiInstance();
  const reqBody = { email, name, family_name }
  try {
    await api.post("/admin/create-pilot", { ...reqBody });
  } catch (error) {
    throw error;
  }
}

export const fetchDataSnapshot = async ({ datatype, userId, timeline }: {
  datatype: string;
  userId: string;
  timeline: number;
}) => {
  const startDate = dayjs().subtract(timeline, "days").format("YYYY-MM-DD");
  const endDate = dayjs().format("YYYY-MM-DD");
  const api = await getInternalApiInstance();
  const reqBody = { datatype, userId, startDate, endDate };
  try {
    const res = await api.post("/admin/data-snapshot", { ...reqBody });
    return res.data.data;
  } catch (error) {
    throw error;
  }
}

export const bulkEmailToUserId = async ({ file }: { file: File }) => {
    const api = await getInternalApiInstance();

    try {
        // Create form data object to send file and parameters
        const formData = new FormData();
        formData.append("file", file);

        const res = await api.post("/admin/bulk-email-to-user-id", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
            responseType: "blob",
        });

        const contentType = res.headers["content-type"];
        if (contentType && contentType.includes("text/csv")) {
            // Create download link and trigger download
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;

            // Get filename from Content-Disposition header or use default
            const contentDisposition = res.headers["content-disposition"];
            const filenameMatch =
                contentDisposition &&
                contentDisposition.match(/filename="?([^"]*)"?/);
            const filename = filenameMatch
                ? filenameMatch[1]
                : `user_ids_${Date.now()}.csv`;

            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Return info about the download
            return {
                success: true,
                downloadTriggered: true,
                message: "CSV download initiated successfully",
            };
        }

        // Handle regular JSON response (shouldn't normally reach here)
        return res.data.data;
    } catch (error) {
        console.error("Error uploading to Amplitude cohort:", error);
        throw error;
    }
};

export const addToActivationDebugGroup = async (userId: string) => {
    const api = await getInternalApiInstance();
    const reqBody = { userId };
    try {
        const res = await api.post("/admin/add-user-to-activation-debug", {
            ...reqBody,
        });
        return res.data.data;
    } catch (error) {
        throw error;
    }
};

export const addToBetaGroup = async (userId: string) => {
    const api = await getInternalApiInstance();
    const reqBody = { userId };
    try {
        const res = await api.post("/admin/beta/add", {
            ...reqBody,
        });
        return res.data.data;
    } catch (error) {
        throw error;
    }
}

export const removeFromBetaGroup = async (userId: string) => {
    const api = await getInternalApiInstance();
    const reqBody = { userId };
    try {
        const res = await api.post("/admin/beta/remove", {
            ...reqBody,
        });
        return res.data.data;
    } catch (error) {
        throw error;
    }
}

export const isUserInBetaGroup = async (userId: string) => {
    const api = await getInternalApiInstance();
    const reqBody = { userId };
    try {
        const res = await api.post("/admin/beta/check", {
            ...reqBody,
        });
        return res.data.data === true;
    } catch (error) {
        throw error;
    }
}

export const generateBetaInviteLink = async (expirySeconds?: number) => {
    const api = await getInternalApiInstance();
    try {
        const res = await api.post("/admin/beta/generate-invite", {
            expirySeconds,
        });
        return res.data.data as { token: string; inviteUrl: string };
    } catch (error) {
        throw error;
    }
}

export const addToPilotGroup = async (userId: string) => {
    const api = await getInternalApiInstance();
    const reqBody = { userId };
    try {
        const res = await api.post("/admin/pilot/add", {
            ...reqBody,
        });
        return res.data.data;
    } catch (error) {
        throw error;
    }
}

export const isUserInPilotGroup = async (userId: string) => {
    const api = await getInternalApiInstance();
    const reqBody = { userId };
    try {
        const res = await api.post("/admin/pilot/check", {
            ...reqBody,
        });
        return res.data.data === true;
    } catch (error) {
        throw error;
    }
}

export const removeFromPilotGroup = async (userId: string) => {
    const api = await getInternalApiInstance();
    const reqBody = { userId };
    try {
        const res = await api.post("/admin/pilot/remove", {
            ...reqBody,
        });
        return res.data.data;
    } catch (error) {
        throw error;
    }
}

export const updateUserHeight = async (userId: string, heightCm: number) => {
    const api = await getInternalApiInstance();
    try {
        const res = await api.patch(`/admin/user/${userId}/height`, {
            heightCm,
        });
        return res.data.data;
    } catch (error) {
        throw error;
    }
}

export const updateUserFirstName = async (userId: string, firstName: string) => {
    const api = await getInternalApiInstance();
    try {
        const res = await api.patch(`/admin/user/${userId}/first-name`, {
            firstName,
        });
        return res.data.data;
    } catch (error) {
        throw error;
    }
}

export const updateUserGender = async (userId: string, gender: string) => {
    const api = await getInternalApiInstance();
    try {
        const res = await api.patch(`/admin/user/${userId}/gender`, {
            gender,
        });
        return res.data.data;
    } catch (error) {
        throw error;
    }
}